import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Inbox, Phone, Mail, FileText, Download } from "lucide-react";
import { Logo, Eyebrow } from "@/components/Reveal";
import ThemeToggle from "@/components/ThemeToggle";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";

const fmt = (iso) =>
    new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });

const inputCls = "w-full bg-paper border border-line rounded-md px-4 py-3 text-sm text-ink focus:outline-none focus:border-copper transition-colors duration-300";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const STATUS_STYLES = {
    draft: "border-line text-mist",
    sent: "border-copper/40 bg-copper-light text-copper",
    accepted: "border-emerald-300 bg-emerald-50 text-emerald-700",
    rejected: "border-red-300 bg-red-50 text-red-700",
};

export default function Portal() {
    const location = useLocation();
    const [authState, setAuthState] = useState(location.state?.user ? "yes" : null);
    const [user, setUser] = useState(location.state?.user || null);
    const [enquiries, setEnquiries] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [waPhone, setWaPhone] = useState("");
    const [waOtp, setWaOtp] = useState("");
    const [waStage, setWaStage] = useState("idle");

    const loadPortal = () => {
        api.get("/portal/enquiries").then((res) => setEnquiries(res.data)).catch(() => setEnquiries([]));
        api.get("/portal/quotes").then((res) => setQuotes(res.data)).catch(() => setQuotes([]));
    };

    const downloadQuote = async (quoteId) => {
        try {
            const res = await api.get(`/portal/quotes/${quoteId}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${quoteId}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Could not download the quotation PDF");
        }
    };

    useEffect(() => {
        if (location.state?.user) {
            loadPortal();
            return;
        }
        (async () => {
            try {
                const r = await api.get("/auth/me");
                setUser(r.data);
                setAuthState("yes");
                loadPortal();
            } catch {
                setAuthState("no");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const googleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/portal";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const waRequest = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post("/auth/whatsapp/request", { phone: waPhone });
            if (res.data.status === "pending_integration") {
                setWaStage("pending");
            } else {
                setWaStage("otp");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not start WhatsApp sign-in");
        }
    };

    const waVerify = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post("/auth/whatsapp/verify", { phone: waPhone, otp: waOtp });
            setUser(res.data);
            setAuthState("yes");
            loadPortal();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Invalid code");
        }
    };

    const logout = async () => {
        await api.post("/auth/logout").catch(() => {});
        setUser(null);
        setAuthState("no");
        setEnquiries([]);
        setQuotes([]);
    };

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="portal-page">
            <Seo title="Customer Portal" noindex path="/portal" />
            <header className="sticky top-0 z-30 backdrop-blur-xl bg-paper/85 border-b border-line">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo size={28} />
                        <span className="font-display font-bold tracking-[0.06em] text-lg text-ink hidden sm:block">Roxy Cables</span>
                        <span className="font-mono text-[10px] tracking-[0.25em] text-copper uppercase ml-2">Customer Portal</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <ThemeToggle />
                        {authState === "yes" && (
                            <button data-testid="portal-logout" onClick={logout} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-copper transition-colors duration-300">
                                <LogOut size={13} /> Logout
                            </button>
                        )}
                        <Link to="/" data-testid="portal-back-link" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-copper transition-colors duration-300">
                            <ArrowLeft size={14} /> Site
                        </Link>
                    </div>
                </div>
            </header>

            <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-14">
                {authState === null && (
                    <p className="font-mono text-sm text-mist" data-testid="portal-loading">Checking your session…</p>
                )}

                {authState === "no" && (
                    <div className="max-w-md mx-auto">
                        <Eyebrow className="mb-4">Secure Sign-In</Eyebrow>
                        <h1 className="font-display font-bold tracking-tight leading-[1.05] text-4xl text-ink mb-4">
                            Your enquiries, <em className="text-copper">in one place.</em>
                        </h1>
                        <p className="text-mist text-sm leading-relaxed mb-8">
                            Sign in to view enquiries linked to your account. Quotations, order history and
                            dispatch status appear here as they're issued.
                        </p>

                        <button
                            data-testid="portal-google-login"
                            onClick={googleLogin}
                            className="w-full rounded-full bg-ink text-white font-mono uppercase tracking-[0.18em] text-[11px] py-4 hover:bg-copper transition-colors duration-300 flex items-center justify-center gap-3 mb-6"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z"/></svg>
                            Continue with Google
                        </button>

                        <div className="flex items-center gap-4 mb-6">
                            <span className="h-px flex-1 bg-line" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim">or</span>
                            <span className="h-px flex-1 bg-line" />
                        </div>

                        <div className="rounded-md border border-line bg-white p-6" data-testid="portal-wa-card">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-4">Sign in with WhatsApp OTP</p>
                            {waStage === "idle" && (
                                <form onSubmit={waRequest} className="space-y-3">
                                    <input data-testid="portal-wa-phone" type="tel" required value={waPhone} onChange={(e) => setWaPhone(e.target.value)} placeholder="WhatsApp number (+91…)" className={inputCls} />
                                    <button data-testid="portal-wa-send" className="w-full rounded-full bg-[#25D366] text-white font-mono uppercase tracking-[0.18em] text-[11px] py-3.5 hover:opacity-90 transition-opacity">
                                        Send Code on WhatsApp
                                    </button>
                                </form>
                            )}
                            {waStage === "pending" && (
                                <p className="text-sm text-mist leading-relaxed" data-testid="portal-wa-pending">
                                    WhatsApp sign-in activates the moment our WhatsApp Business API connection is
                                    approved. Until then, please use Google above — it takes ten seconds.
                                </p>
                            )}
                            {waStage === "otp" && (
                                <form onSubmit={waVerify} className="space-y-3">
                                    <input data-testid="portal-wa-otp" required value={waOtp} onChange={(e) => setWaOtp(e.target.value)} placeholder="6-digit code" className={inputCls} />
                                    <button data-testid="portal-wa-verify" className="w-full rounded-full bg-copper text-white font-mono uppercase tracking-[0.18em] text-[11px] py-3.5 hover:bg-copper-bright transition-colors">
                                        Verify &amp; Sign In
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                )}

                {authState === "yes" && user && (
                    <div>
                        <div className="flex flex-wrap items-center gap-4 mb-10">
                            {user.picture ? (
                                <img src={user.picture} alt="" className="w-12 h-12 rounded-full border border-line" />
                            ) : (
                                <span className="w-12 h-12 rounded-full bg-copper-light border border-copper/30 flex items-center justify-center font-display font-bold text-copper text-lg">
                                    {(user.name || user.phone || "R")[0].toUpperCase()}
                                </span>
                            )}
                            <div>
                                <h1 className="font-display font-bold text-3xl text-ink" data-testid="portal-welcome">
                                    Welcome{user.name ? `, ${user.name.split(" ")[0]}` : ""}.
                                </h1>
                                <p className="font-mono text-[11px] text-dim">{user.email || user.phone}</p>
                            </div>
                        </div>

                        <Eyebrow className="mb-5">My Enquiries</Eyebrow>
                        {enquiries.length === 0 ? (
                            <div className="rounded-md border border-line bg-white p-10 text-center" data-testid="portal-empty">
                                <Inbox size={26} className="text-copper mx-auto mb-3" />
                                <p className="font-display font-semibold text-xl text-ink mb-2">No enquiries linked yet</p>
                                <p className="text-mist text-sm max-w-sm mx-auto">
                                    Enquiries you place with {user.email || "your number"} appear here automatically — including BOQ uploads and quote requests.
                                </p>
                            </div>
                        ) : (
                            <div className="rounded-md border border-line bg-white divide-y divide-line overflow-hidden" data-testid="portal-enquiry-list">
                                {enquiries.map((e, i) => (
                                    <div key={i} className="p-5" data-testid={`portal-enquiry-${i}`}>
                                        <div className="flex flex-wrap justify-between gap-2 mb-2">
                                            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-copper bg-copper-light border border-copper/30 px-2 py-1 rounded-full">{e.interest}</span>
                                            <span className="font-mono text-[10px] text-dim">{fmt(e.created_at)}</span>
                                        </div>
                                        <p className="text-sm text-mist whitespace-pre-line leading-relaxed">{e.message || "—"}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        <Eyebrow className="mb-5 mt-12">My Quotations</Eyebrow>
                        {quotes.length === 0 ? (
                            <div className="rounded-md border border-line bg-white p-8 text-center mb-8" data-testid="portal-quotes-empty">
                                <FileText size={24} className="text-copper mx-auto mb-3" />
                                <p className="text-mist text-sm max-w-sm mx-auto">
                                    Priced quotations issued against your enquiries appear here, ready to download as PDF.
                                </p>
                            </div>
                        ) : (
                            <div className="rounded-md border border-line bg-white divide-y divide-line overflow-hidden mb-8" data-testid="portal-quotes-list">
                                {quotes.map((q) => (
                                    <div key={q.quote_id} className="p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`portal-quote-${q.quote_id}`}>
                                        <div>
                                            <div className="flex items-center gap-3 mb-1">
                                                <span className="font-mono text-xs text-ink font-semibold">{q.quote_id}</span>
                                                <span className={`font-mono text-[9px] tracking-[0.15em] uppercase border px-2 py-0.5 rounded-full ${STATUS_STYLES[q.status] || STATUS_STYLES.draft}`}>{q.status}</span>
                                            </div>
                                            <p className="font-mono text-[10px] text-dim">{fmt(q.created_at)} · {q.items?.length || 0} item{(q.items?.length || 0) !== 1 ? "s" : ""}</p>
                                        </div>
                                        <div className="flex items-center gap-5">
                                            <span className="font-mono text-sm text-copper font-semibold">{inr(q.total)} <span className="text-dim text-[9px] font-normal">incl. GST</span></span>
                                            <button
                                                data-testid={`portal-quote-pdf-${q.quote_id}`}
                                                onClick={() => downloadQuote(q.quote_id)}
                                                className="flex items-center gap-2 rounded-full border border-line font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2 text-mist hover:border-copper hover:text-copper transition-colors"
                                            >
                                                <Download size={12} /> PDF
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="mt-8 rounded-md border border-line bg-white p-6 flex flex-wrap gap-x-8 gap-y-2 font-mono text-xs text-mist" data-testid="portal-contact">
                            <span className="flex items-center gap-2"><Phone size={12} className="text-copper" /> +91 900 81 77 176</span>
                            <span className="flex items-center gap-2"><Mail size={12} className="text-copper" /> sales@roxycables.com</span>
                            <span className="text-dim">Quotations &amp; dispatch status appear here as issued.</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
